import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import { Grid, Typography, TextField, Button, IconButton, InputAdornment } from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EditOutlined as EditOutlinedIcon,
  MailOutline as MailOutlineIcon,
  LocalPhoneOutlined as LocalPhoneOutlinedIcon,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import * as yup from "yup";
import fileUploadApi from "Services/ImageUpload";
import settingsApi from "Services/Settings";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { triggerHeaderProfile } from "Redux/ProfileSettings/actions";

const ProfileSettings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const triggerHeaderState = useSelector(
    (state) => state?.ProfileSettingsReducer?.triggerHeader
  );

  const [profileDetails, setProfileDetails] = useState(null);
  const [userId, setuserId] = useState("");
  const [passengerimage, setpassengerimage] = useState("/assets/images/Admindefaultimg.png");

  // Upload admin profile image
  const uploadProfileImage = (acceptedFiles) => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Profile image uploaded successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik1.setFieldValue("adminImage", res?.data?.data?.image);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.data?.msg || "Error while uploading image",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const formData = new FormData();
    acceptedFiles.forEach((file) => {
      formData.append("image", file);
    });
    formData.append("userId", profileDetails?._id || userId);

    fileUploadApi.profileimageupload(formData).then(onSuccess, onFailure);
  };

  // Max file size
  const maxFileSize = 5242880;
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // File length validator
  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert(`File size is larger than ${formatBytes(maxFileSize)}`);
      return {
        code: "file-too-large",
        message: `File size is larger than ${formatBytes(maxFileSize)}`,
      };
    }
    return null;
  };

  // Dropzone props
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      let fileLength = acceptedFiles.map((file) => file.size);
      if (acceptedFiles && fileLength < maxFileSize) {
        uploadProfileImage(acceptedFiles);
      }
    },
  });

  useEffect(() => {
    const setDefaultImageAsFile = async () => {
      try {
        const response = await fetch("/assets/images/Admindefaultimg.png");
        const blob = await response.blob();

        const file = new File([blob], "default-passenger.png", {
          type: blob.type,
        });

        const preview = URL.createObjectURL(file);
        setpassengerimage(preview);

        formik1.setFieldValue("adminImage", file);
      } catch (error) {
        console.error("Error setting default image:", error);
      }
    };

    setDefaultImageAsFile();
  }, []);

  // Save or Edit profile details
  const handleBasicInfo = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      firstName: formValues?.firstName,
      lastName: formValues?.lastName,
      user_name: `${formValues?.firstName} ${formValues?.lastName}`.trim(),
      email: formValues?.email,
      mobile: formValues?.mobileNo,
      userID: userId,
    };
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "User details updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        setProfileDetails([]);
        formik1.resetForm();
        getProfileDetails();
        dispatch(triggerHeaderProfile(!triggerHeaderState));
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while updating user details",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    settingsApi.updateProfileDetails({ ...data }).then(onSuccess, onFailure);
  };

  const formik1 = useFormik({
    initialValues: {
      adminImage: "",
      firstName: "",
      lastName: "",
      email: "",
      mobileNo: "",
      userID: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      adminImage: yup.string().nullable(),
      firstName: yup
        .string()
        .required("First name is required")
        .max(30, "Max 30 characters")
        .trim()
        .nullable(),
      lastName: yup
        .string()
        .required("Last name is required")
        .max(30, "Max 30 characters")
        .trim()
        .nullable(),
      email: yup.string().email("Enter valid email").nullable(),
      mobileNo: yup
        .string()
        .required("Mobile number is required")
        .matches(/^[0-9]+$/, "Only Numbers are allowed")
        .max(10, `Max 10 digits are allowed`)
        .min(10, `Must be exactly 10 numbers`)
        .nullable(),
    }),
    onSubmit: handleBasicInfo,
  });

  // Update Password
  const handlePassword = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      newPassword: formValues?.confirmPassword,
      userID: userId,
    };
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Updated password successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik2?.resetForm();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Error while updating password",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    settingsApi.updatePassword({ ...data }).then(onSuccess, onFailure);
  };

  const formik2 = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      newPassword: yup.string().required("New password is required"),
      confirmPassword: yup
        .string()
        .required("Confirm password is required")
        .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
    }),
    onSubmit: handlePassword,
  });

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  // Get profile details api
  const getProfileDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setProfileDetails(res?.data?.data);
      } else {
        setProfileDetails([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    settingsApi.getProfileDetails({}).then(onSuccess, onFailure);
  }, [dispatch]);

  useEffect(() => {
    getProfileDetails();
  }, [getProfileDetails]);

  // Set profile values for edit
  useEffect(() => {
    if (!_.isEmpty(profileDetails)) {
      if (
        profileDetails?.image !== null &&
        profileDetails?.image !== "" &&
        profileDetails?.image !== "undefined"
      ) {
        formik1.setFieldValue("adminImage", profileDetails?.image);
        setpassengerimage(profileDetails?.image);
      }

      const nameParts = profileDetails?.user_name ? profileDetails.user_name.trim().split(" ") : [];
      const fName = profileDetails?.firstName || (nameParts.length > 0 ? nameParts[0] : "");
      const lName = profileDetails?.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");

      formik1.setFieldValue("firstName", fName);
      formik1.setFieldValue("lastName", lName);
      formik1.setFieldValue("email", profileDetails?.email);
      formik1.setFieldValue("mobileNo", profileDetails?.mobileNumber);
      setuserId(profileDetails?._id);
      formik2.setFieldValue("newPassword", "");
      formik2.setFieldValue("confirmPassword", "");
    }
  }, [profileDetails]);

  const handleUnifiedUpdate = async () => {
    const errors1 = await formik1.validateForm();
    formik1.setTouched({
      firstName: true,
      lastName: true,
      email: true,
      mobileNo: true,
    });

    const isPasswordFilled = formik2.values.newPassword || formik2.values.confirmPassword;
    let errors2 = {};
    if (isPasswordFilled) {
      errors2 = await formik2.validateForm();
      formik2.setTouched({
        newPassword: true,
        confirmPassword: true,
      });
    }

    const isFormik1Valid = Object.keys(errors1).length === 0;
    const isFormik2Valid = !isPasswordFilled || Object.keys(errors2).length === 0;

    if (isFormik1Valid && isFormik2Valid) {
      formik1.submitForm();
      if (isPasswordFilled) {
        formik2.submitForm();
      }
    }
  };

  return (
    <div className={classes.cardContainer}>
      <div className={classes.headerContainer}>
        <Typography className={classes.headerTitle}>My Profile</Typography>
        <div className={classes.breadcrumbContainer}>
          <Typography className={classes.breadcrumbLink}>Admin</Typography>
          <Typography className={classes.breadcrumbLink}>/</Typography>
          <Typography className={classes.breadcrumbActive}>Profile</Typography>
        </div>
      </div>

      <Grid container spacing={4} className={classes.mainContent}>
        {/* Left Column - Profile Photo */}
        <Grid item xs={12} md={3} className={classes.leftColumn}>
          <div className={classes.photoSection}>
            <div className={classes.avatarWrapper}>
              <img
                src={
                  passengerimage
                    ? passengerimage
                    : "/assets/images/Admindefaultimg.png"
                }
                alt="avatar"
                className={classes.avatar}
              />
              <div
                {...getRootProps()}
                onClick={open}
                className={classes.editBadge}
              >
                <input {...getInputProps()} />
                <EditOutlinedIcon className={classes.editBadgeIcon} />
              </div>
            </div>
            <Typography className={classes.photoTitle}>Profile Photo</Typography>
            <Typography className={classes.photoSubtitle}>JPG, PNG up to 5MB</Typography>
          </div>
        </Grid>

        {/* Right Column - Forms */}
        <Grid item xs={12} md={9} className={classes.rightColumn}>
          {/* Basic Information */}
          <Typography variant="h4" className={classes.sectionTitle}>Basic Information</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <label className={classes.label}>First Name</label>
                <TextField
                  name="firstName"
                  id="firstName"
                  placeholder="e.g. John"
                  value={formik1?.values?.firstName}
                  onChange={formik1?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  error={formik1?.errors?.firstName && formik1?.touched?.firstName}
                  helperText={
                    formik1?.errors?.firstName &&
                    formik1?.touched?.firstName &&
                    formik1?.errors?.firstName
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <label className={classes.label}>Last Name</label>
                <TextField
                  name="lastName"
                  id="lastName"
                  placeholder="e.g. Doe"
                  value={formik1?.values?.lastName}
                  onChange={formik1?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  error={formik1?.errors?.lastName && formik1?.touched?.lastName}
                  helperText={
                    formik1?.errors?.lastName &&
                    formik1?.touched?.lastName &&
                    formik1?.errors?.lastName
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <label className={classes.label}>Email Address</label>
                <TextField
                  name="email"
                  id="email"
                  placeholder="john.doe@example.com"
                  value={formik1?.values?.email}
                  onChange={formik1?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  error={formik1?.errors?.email && formik1?.touched?.email}
                  helperText={
                    formik1?.errors?.email &&
                    formik1?.touched?.email &&
                    formik1?.errors?.email
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <label className={classes.label}>Mobile Number</label>
                <TextField
                  name="mobileNo"
                  id="mobileNo"
                  placeholder="e.g. 9876543210"
                  value={formik1?.values?.mobileNo}
                  onChange={formik1?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  error={formik1?.errors?.mobileNo && formik1?.touched?.mobileNo}
                  helperText={
                    formik1?.errors?.mobileNo &&
                    formik1?.touched?.mobileNo &&
                    formik1?.errors?.mobileNo
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalPhoneOutlinedIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </Grid>
          </Grid>

          {/* Change Password */}
          <Typography variant="h4" className={classes.passwordSectionTitle}>Change password</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <label className={classes.label}>Password</label>
                <TextField
                  name="newPassword"
                  id="newPassword"
                  placeholder="********"
                  autoComplete="new-password"
                  value={formik2?.values?.newPassword}
                  onChange={formik2?.handleChange}
                  type={showNewPassword ? "text" : "password"}
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  error={formik2?.errors?.newPassword && formik2?.touched?.newPassword}
                  helperText={
                    formik2?.errors?.newPassword &&
                    formik2?.touched?.newPassword &&
                    formik2?.errors?.newPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={handleToggleNewPasswordVisibility}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <label className={classes.label}>Confirm Password</label>
                <TextField
                  name="confirmPassword"
                  id="confirmPassword"
                  placeholder="********"
                  autoComplete="confirm-password"
                  value={formik2?.values?.confirmPassword}
                  onChange={(e) => {
                    formik2.handleChange(e);
                    formik2.validateField("confirmPassword");
                  }}
                  type={showConfirmPassword ? "text" : "password"}
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  error={
                    formik2?.errors?.confirmPassword && formik2?.touched?.confirmPassword
                  }
                  helperText={
                    formik2?.errors?.confirmPassword &&
                    formik2?.touched?.confirmPassword &&
                    formik2?.errors?.confirmPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleToggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <div className={classes.btnWrapper}>
            <Button variant="contained" onClick={handleUnifiedUpdate} className={classes.updateBtn}>
              Update
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default ProfileSettings;

